import {Template} from "../../../components/template/template.jsx";
import Button from "../../../components/button/button.jsx";
import {FaFileExcel, FaPrint} from "react-icons/fa";
import HeaderTemplateButton from "../../../components/button/header-template-button.jsx";
import React, {useEffect, useMemo, useRef, useState} from "react";
import PrintTemplate from "../../../components/reports/print-reports/print-template.jsx";
import {Card} from "../../../components/card/card.jsx";
import PrintableTableRaw from "../../../components/printable-table/printable-table-raw.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import {exportXlsx} from "../../../utils/excel-export-utils.js";
import TableColumnVisibility from "../../../components/reports/table-column-visibility/table-column-visibility.jsx";

const citiesHeaders = [
  {headerName: "Município", field: "name", columnId: "municipality", flex: 1},
  {headerName: "Status", field: "status", columnId: "status", flex: 1},
]

const resultHeaders = [
  {headerName: "Data/hora", field: "date", columnId: "date", flex: 2},
  {headerName: "Município", field: "city", columnId: "municipality", flex: 4},
  {headerName: "Sentido", field: "laneDescLang", columnId: "direction", flex: 3},
  {headerName: "Local", field: "customLocal", columnId: "local", flex: 10},
]

const initialTableColumns = {
  municipality: {label: "Município", visible: true},
  status: {label: "Status", visible: true},
  date: {label: "Data/hora", visible: true},
  direction: {label: "Sentido", visible: true},
  local: {label: "Local", visible: true},
};

const SentryCloudSearchInfo = () => {
  const [entity, setEntity] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [tableColumns, setTableColumns] = useState(() => initialTableColumns);
  const printButton = useRef(null);
  const [{ cities, result }] = useState(() => {
    const savedData = sessionStorage.getItem('sentry-cloud-search-info');
    if (savedData) {
      // sessionStorage.removeItem('sentry-cloud-search-info');
      return JSON.parse(savedData);
    }
    return { cities: [], result: [] };
  });
  const [filters] = useState(() => {
    const savedData = sessionStorage.getItem('sentry-cloud-search-info-filters');
    if (savedData) {
      // sessionStorage.removeItem('sentry-cloud-search-info-filters');
      return JSON.parse(savedData);
    }
    return null;
  });

  const printableTableFormatters = useMemo(() => ({
    customLocal: row => {
      const { cpName, rtDescr, laneObs } = row;
      if(cpName && rtDescr) {
        return `${cpName} - ${rtDescr} ${laneObs ? ' - ' + laneObs : ''}`;
      }
      return cpName ?? "";
    }
  }), []);

  const visibleCitiesHeaders = useMemo(
    () => citiesHeaders.filter(header => tableColumns?.[header.columnId]?.visible !== false),
    [tableColumns],
  );

  const visibleResultHeaders = useMemo(
    () => resultHeaders.filter(header => tableColumns?.[header.columnId]?.visible !== false),
    [tableColumns],
  );

  const hasVisibleResultColumns = visibleResultHeaders.length > 0;

  const getCellValue = (row, header, formatters = {}) => {
    if (formatters[header.field]) return formatters[header.field](row);
    return row[header.field] ?? "";
  };

  const handleExport = async () => {
    if (!hasVisibleResultColumns) return;

    const buildExportRows = (rows, headers, formatters = {}) => rows.map(row => (
      Object.fromEntries(
        headers.map(header => [header.headerName, getCellValue(row, header, formatters)])
      )
    ));

    const dataCitiesExcel = buildExportRows(cities, visibleCitiesHeaders);
    const dataResultExcel = buildExportRows(result, visibleResultHeaders, printableTableFormatters);

    const dateStr = new Date().toISOString().split('T')[0];
    await exportXlsx({
      fileName: `report_${dateStr}.xlsx`,
      sheets: [
        ...(cities.length && visibleCitiesHeaders.length ? [{ name: "Corporações", data: dataCitiesExcel }] : []),
        ...(result.length && visibleResultHeaders.length ? [{ name: "Passagens", data: dataResultExcel }] : [])
      ]
    });
  };

  const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    reason: "Motivo",
    plate: "Placa"
  };


  useEffect(() => {
    if (!filters) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
      }, filterLabels)
    );

  }, [filters]);

  return(
    <Template isLoading={isLoading}>
      <Template.Header>
        <Template.Title>
          Pesquisa de passagens - SentryCloud
        </Template.Title>
        <div className="flex gap-2">
          {(cities.length > 0 || result.length > 0) && (
            <TableColumnVisibility
              columns={tableColumns}
              setColumns={setTableColumns}
              onOpenChange={(isOpen) => {
                if (isOpen) {
                  document.dispatchEvent(new CustomEvent('report-dropdown-open', {
                    detail: 'columns'
                  }));
                }
              }}
            />
          )}
          <Button target="success" onClick={handleExport} disabled={!hasVisibleResultColumns}><FaFileExcel/>Exportar</Button>
          <Button title="Imprimir" onClick={() => printButton.current?.print()}><FaPrint/></Button>
          <HeaderTemplateButton selected={entity} setSelected={setEntity}/>
        </div>
      </Template.Header>
      <Template.Body>
        <Card>
          <Card.Body className="overflow-y-auto">
            <div className={`h-[calc(100vh-91px)] ${!hasVisibleResultColumns ? "mt-[-4px]" : ""}`}>
              <PrintTemplate entity={entity} title="Pesquisa de passagens - SentryCloud" setIsPrinting={setIsLoading} ref={printButton}>
                <div id="filters-print" className={'transform !-translate-y-[8.5px]'}></div>
                {filters && <div className="print:w-full print:border-b print:border-b-[#d9dada] print:mb-2"></div>}
                {hasVisibleResultColumns ? (
                  <>
                    {!!cities?.length && !!visibleCitiesHeaders.length && (
                      <div className="flex flex-col gap-2">
                        <div className="text-[17px] font-bold">Corporações</div>
                        <PrintableTableRaw rowData={cities} columnDefs={visibleCitiesHeaders} formatters={[]}/>
                        <div className="w-full border-b border-b-[#d9dada] my-2"></div>
                      </div>
                    )}
                    {!!result?.length && (
                      <div className="flex flex-col gap-2 mt-4">
                        <div className="text-[17px] font-bold">Passagens</div>
                        <PrintableTableRaw rowData={result} columnDefs={visibleResultHeaders} formatters={printableTableFormatters}/>
                      </div>
                    )}
                  </>
                ) : (
                  <div id="noDataMessage" className="alert alert-info p-2 h6 text-center">
                    Todas as colunas foram ocultadas.
                  </div>
                )}
              </PrintTemplate>
              <div className="print:hidden p-2"></div>
            </div>
          </Card.Body>
        </Card>
      </Template.Body>
    </Template>
  )
}

export default SentryCloudSearchInfo;
